import useSWR from "swr";
import { message } from "antd";
import * as Sentry from "@sentry/nextjs";
import { FlowOfferData } from "../steps/cross-sell";
import { useAuthToken } from "../passwordless-auth";
import { useFetcher } from "./";
import { api } from "@dreambigger/shared/src/api/acquire";
import { crossSellPaths } from "./cross-sell";

/**
 * @param cacheKey changes whenever the answers the offers' conditions are
 *   evaluated against change. The server decides which offers match by running
 *   each offer's `conditions` over the application's current responses, so the
 *   same URL legitimately returns different offers from one step to the next.
 *   Without this in the key SWR serves the previous answer: pick a product, come
 *   back, and the step still sees the empty list from before — and an empty list
 *   makes it auto-advance, so it skips every time until a full page reload drops
 *   the cache.
 */
export default function useCrossSell(
  applicationId: string | undefined,
  financialInstitutionId: string,
  maxOffers?: number,
  cacheKey?: string
) {
  const tokenHelper = useAuthToken(financialInstitutionId);
  const { fetchWithToken } = useFetcher(api, financialInstitutionId);

  const urlWithParams = applicationId
    ? `${crossSellPaths.getProductsByApplication(applicationId)}${
        maxOffers ? `?maxOffers=${maxOffers}` : ""
      }`
    : null;

  const { data, isValidating, error } = useSWR<FlowOfferData[]>(
    applicationId && !tokenHelper.jwtIsExpired()
      ? [urlWithParams, tokenHelper.getJwtToken(), cacheKey ?? ""]
      : null,
    fetchWithToken,
    {
      onError: (error) => {
        console.error("Error loading cross-sell products:", error);
        console.error("Error details:", error.response?.data || error.message);
        message.error("Unable to load product offers");
        Sentry.captureException(error);
      },
      // Short enough to dedupe a double render, not long enough to outlive the
      // answers the offers were matched against. The old minute meant a revisit
      // inside that window reused the previous verdict.
      dedupingInterval: 2000,
      revalidateOnFocus: false,
      // Every fetch writes `flow_offer_logs` rows, so a reconnect should not
      // silently re-serve and re-log the same offers.
      revalidateOnReconnect: false,
    }
  );

  return {
    data,
    isValidating,
    error,
  };
}
